// resources/js/components/Balancer/index.js

const SYMBOL_KEY = '__wrap_b';
const SYMBOL_NATIVE_KEY = '__wrap_n';
const SYMBOL_OBSERVER_KEY = '__wrap_o';

const IS_SERVER = typeof window === 'undefined';

let ID = 0;
const genId = () => ++ID;

const relayout = (id, ratio, wrapper) => {
    wrapper = wrapper || document.querySelector(`[data-br="${id}"]`);
    const container = wrapper?.parentElement;

    if (!container) return;

    const update = (width) => (wrapper.style.maxWidth = width + 'px');

    // Reset wrapper width
    wrapper.style.maxWidth = '';

    // Get the initial container size
    const width = container.clientWidth;
    const height = container.clientHeight;

    // Synchronously do binary search and calculate the layout
    let lower = width / 2 - 0.25;
    let upper = width + 0.5;
    let middle;

    if (width) {
        // Ensure we don't search widths lower than when the text overflows
        update(lower);
        lower = Math.max(wrapper.scrollWidth, lower);

        while (lower + 1 < upper) {
            middle = Math.round((lower + upper) / 2);
            update(middle);
            if (container.clientHeight === height) {
                upper = middle;
            } else {
                lower = middle;
            }
        }

        // Update the wrapper width
        update(upper * ratio + width * (1 - ratio));
    }

    // Create a new observer if we don't have one
    if (!wrapper[SYMBOL_OBSERVER_KEY]) {
        if (typeof ResizeObserver !== 'undefined') {
            wrapper[SYMBOL_OBSERVER_KEY] = new ResizeObserver(() => {
                window[SYMBOL_KEY](0, +wrapper.dataset.brr, wrapper);
            });
            wrapper[SYMBOL_OBSERVER_KEY].observe(container);
        } else if (process.env.NODE_ENV === 'development') {
            console.warn(
                'The browser you are using does not support the ResizeObserver API. ' + 'Please consider adding a polyfill for this API to avoid potential layout shifts or upgrade your browser.'
            );
        }
    }
};

const RELAYOUT_STR = relayout.toString();
const isTextWrapBalanceSupported = `(self.CSS&&CSS.supports("text-wrap","balance")?1:2)`;

// Inject the global script once
const injectGlobalScript = (nonce) => {
    if (IS_SERVER) return;
    if (window[SYMBOL_NATIVE_KEY] !== undefined) return; // Already injected

    const script = document.createElement('script');
    if (nonce) script.nonce = nonce;
    script.innerHTML = `self.${SYMBOL_NATIVE_KEY}=self.${SYMBOL_NATIVE_KEY}||${isTextWrapBalanceSupported};self.${SYMBOL_KEY}=${RELAYOUT_STR};`;
    document.head.appendChild(script);
};

/**
 * BalancerProvider - Optional provider to inject the global relayout function
 */
export const BalancerProvider = {
    name: 'BalancerProvider',

    provide() {
        return {
            balancerContext: {
                preferNative: this.preferNative,
                hasProvider: true
            }
        };
    },

    props: {
        preferNative: {
            type: Boolean,
            default: true
        },
        nonce: {
            type: String,
            default: undefined
        }
    },

    created() {
        injectGlobalScript(this.nonce);
    },

    render(h) {
        return h('div', { style: { display: 'contents' } }, this.$slots.default);
    }
};

/**
 * Balancer - Text balancing component
 */
export const Balancer = {
    name: 'Balancer',

    inject: {
        balancerContext: {
            default: () => ({
                preferNative: true,
                hasProvider: false
            })
        }
    },

    props: {
        /**
         * The HTML tag to use for the wrapper element.
         * @default 'span'
         */
        as: {
            type: String,
            default: 'span'
        },
        /**
         * The balance ratio of the wrapper width (0 <= ratio <= 1).
         * 0 means the wrapper width is the same as the container width (no balance, browser default).
         * 1 means the wrapper width is the minimum (full balance, most compact).
         * @default 1
         */
        ratio: {
            type: Number,
            default: 1
        },
        /**
         * An option to skip the re-balance logic
         * and use the native CSS text-balancing if supported.
         * @default true
         */
        preferNative: {
            type: Boolean,
            default: null // null means inherit from provider
        },
        /**
         * The nonce attribute to allowlist inline script injection
         */
        nonce: {
            type: String,
            default: undefined
        }
    },

    data() {
        return {
            id: `rwb-${genId().toString(32)}`
        };
    },

    computed: {
        preferNativeBalancing() {
            return this.preferNative !== null ? this.preferNative : this.balancerContext.preferNative;
        },

        wrapperStyle() {
            return {
                display: 'inline-block',
                verticalAlign: 'top',
                textDecoration: 'inherit',
                textWrap: this.preferNativeBalancing ? 'balance' : 'initial'
            };
        }
    },

    mounted() {
        // Inject global script if no provider
        if (!this.balancerContext.hasProvider) {
            injectGlobalScript(this.nonce);
        }

        this.doRelayout();
    },

    updated() {
        this.doRelayout();
    },

    beforeDestroy() {
        this.cleanupObserver();
    },

    methods: {
        doRelayout() {
            // Skip if browser supports text-balancing natively
            if (this.preferNativeBalancing && window[SYMBOL_NATIVE_KEY] === 1) {
                return;
            }

            const wrapper = this.$refs.wrapper;
            if (wrapper) {
                window[SYMBOL_KEY] = relayout;
                relayout(0, this.ratio, wrapper);
            }
        },

        cleanupObserver() {
            // Skip if browser supports text-balancing natively
            if (this.preferNativeBalancing && window[SYMBOL_NATIVE_KEY] === 1) {
                return;
            }

            const wrapper = this.$refs.wrapper;
            if (!wrapper) return;

            const resizeObserver = wrapper[SYMBOL_OBSERVER_KEY];
            if (resizeObserver) {
                resizeObserver.disconnect();
                delete wrapper[SYMBOL_OBSERVER_KEY];
            }
        }
    },

    watch: {
        ratio() {
            this.doRelayout();
        },
        preferNativeBalancing() {
            this.doRelayout();
        }
    },

    render(h) {
        return h(
            this.as,
            {
                ref: 'wrapper',
                attrs: {
                    'data-br': this.id,
                    'data-brr': this.ratio
                },
                style: this.wrapperStyle
            },
            this.$slots.default
        );
    }
};

export default Balancer;
